import useDeviceDimensions from "hooks/useDeviceDimensions";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.bs5.css";

interface Props {
  contestEntries: ContestEntryOption[];
  selectedEntry: ContestEntryOption | undefined;
  setEntry: (entry: ContestEntryOption | undefined) => void;
  disabled: boolean;
}

export interface ContestEntryOption {
  contestEntryId: string;
  contestEntryCreatedAt: string;
  contestId: string;
  contestName: string;
}

const ContestEntrySelector = ({
                                contestEntries,
                                selectedEntry,
                                setEntry,
                                disabled,
                              }: Props) => {
  const { isMobile } = useDeviceDimensions();

  const groupEntriesByContest = (entries: ContestEntryOption[]): Record<string, ContestEntryOption[]> => {
    return entries.reduce<Record<string, ContestEntryOption[]>>((acc, entry) => {
      if (!acc[entry.contestId]) {
        acc[entry.contestId] = [];
      }
      acc[entry.contestId].push(entry);
      return acc;
    }, {});
  };

  const sortByCreatedAt = (entries: ContestEntryOption[], ascending = true): ContestEntryOption[] => {
    return [...entries].sort((a, b) => {
      const dateA = Date.parse(a.contestEntryCreatedAt);
      const dateB = Date.parse(b.contestEntryCreatedAt);
      return ascending ? dateA - dateB : dateB - dateA;
    });
  };

  const entriesGroupedByContest = groupEntriesByContest(contestEntries);

  const generateLabel = (entry: ContestEntryOption): string => {
    const entriesForContest = sortByCreatedAt(entriesGroupedByContest[entry.contestId], true);
    const entryNumber = entriesForContest.indexOf(entry) + 1;

    return entriesForContest.length > 1
      ? `${entry.contestName} - Entry #${entryNumber}`
      : entry.contestName;
  };

  const handleContestSelected = (selectedOptions: any[]): void => {
    if (selectedOptions.length === 0) {
      setEntry(undefined);
      return;
    }

    const selected = contestEntries.find(entry => entry.contestEntryId === selectedOptions[0]?.id);
    setEntry(selected);
  };

  const options = sortByCreatedAt(contestEntries, false).map(entry => ({
    id: entry.contestEntryId,
    label: generateLabel(entry),
  }));

  return (
    <div style={{ width: isMobile ? "100%" : "50%", padding: 5 }}>
      <Typeahead
        id="contest-entry-filter"
        options={options}
        placeholder="Select Contest Entry"
        disabled={disabled}
        clearButton
        onChange={handleContestSelected}
        selected={selectedEntry ? [{ id: selectedEntry.contestEntryId, label: generateLabel(selectedEntry) }] : []}
        labelKey="label"
      />
    </div>
  );
};

export default ContestEntrySelector;
